import { useEffect, useMemo, useState } from 'react';
import { BookOpen, Sparkles } from 'lucide-react';
import { Button, Modal, useToast } from '../../../ui';
import type { Task } from '../../../features/tasks/types';
import type { EstimateInputs } from '../../../features/sprintboard/engine/estimation';
import {
  blankEstimateInputs,
  effectiveEstimate,
  inputsFromSuggestion,
  runEstimate,
  validateOverride,
} from '../../../features/sprintboard/estimate/taskEstimateModel';
import { friendlyEstimateError } from '../../../features/sprintboard/estimate/aiEstimate';
import { useTaskEstimate } from '../../../features/sprintboard/estimate/useTaskEstimate';
import type {
  EstimateOverride,
  EstimateSource,
  TaskEstimate,
} from '../../../features/sprintboard/estimate/types';
import type { KbSource } from '../../../api';
import { EstimateWizard } from './EstimateWizard';

export interface EstimateFlowModalProps {
  open: boolean;
  onClose: () => void;
  /** The task being estimated. */
  task: Task | null;
  /** When re-estimating, the existing estimate to seed + update in place. */
  existingEstimate?: TaskEstimate | null;
  /** Optional linked-entity names, passed to Vector for better context. */
  goalName?: string;
  workstreamName?: string;
  serviceName?: string;
  /** Called after the estimate is saved + mirrored onto the task. */
  onSaved: (task: Task, estimate: TaskEstimate) => void;
}

/** Maps an existing estimate's stored inputs back into engine inputs. */
function inputsFromEstimate(estimate: TaskEstimate): EstimateInputs {
  return {
    changeType: estimate.changeType,
    complexity: estimate.complexity ?? [],
    extraWork: estimate.extraWork ?? [],
    unknowns: estimate.unknowns,
    customChangeType: estimate.customChangeType,
    customBaseDays: estimate.customBaseDays,
  };
}

/**
 * The estimate experience: the compact 4-step wizard, plus the ✨ Estimate with
 * Vector shortcut. Vector proposes the wizard inputs (never the number); the
 * deterministic engine turns them into effort / story points / confidence, and
 * the engineer can Accept or Modify. Saving writes an estimate linked to the
 * task and mirrors the headline numbers back onto it.
 */
export function EstimateFlowModal({
  open,
  onClose,
  task,
  existingEstimate,
  goalName,
  workstreamName,
  serviceName,
  onSaved,
}: EstimateFlowModalProps) {
  const { toast } = useToast();
  const { save, suggest } = useTaskEstimate();

  const [inputs, setInputs] = useState<EstimateInputs>(blankEstimateInputs());
  const [source, setSource] = useState<EstimateSource>('manual');
  const [rationale, setRationale] = useState<string[] | undefined>();
  const [kbSources, setKbSources] = useState<KbSource[]>([]);
  const [grounded, setGrounded] = useState(false);
  const [override, setOverride] = useState<EstimateOverride | undefined>();
  const [saving, setSaving] = useState(false);
  const [aiLoading, setAiLoading] = useState(false);
  const [aiError, setAiError] = useState<string | null>(null);
  const [resetToken, setResetToken] = useState(0);
  const [goToResultToken, setGoToResultToken] = useState(0);

  // (Re)seed the wizard whenever it opens for a task.
  useEffect(() => {
    if (!open) return;
    if (existingEstimate) {
      setInputs(inputsFromEstimate(existingEstimate));
      setSource(existingEstimate.source);
      setRationale(existingEstimate.rationale);
      setKbSources(existingEstimate.sources ?? []);
      setGrounded((existingEstimate.sources ?? []).length > 0);
      setOverride(existingEstimate.override ?? undefined);
    } else {
      setInputs(blankEstimateInputs());
      setSource('manual');
      setRationale(undefined);
      setKbSources([]);
      setGrounded(false);
      setOverride(undefined);
    }
    setAiError(null);
    setResetToken((t) => t + 1);
  }, [open, existingEstimate]);

  const result = useMemo(() => runEstimate(inputs), [inputs]);

  const close = () => {
    if (saving || aiLoading) return;
    onClose();
  };

  const onWizardChange = (next: EstimateInputs) => {
    setInputs(next);
    // Any manual edit means the number is no longer purely Vector's suggestion.
    if (source === 'ai') setSource('manual');
  };

  const runVector = async () => {
    if (!task) return;
    setAiLoading(true);
    setAiError(null);
    try {
      const suggestion = await suggest({
        title: task.title,
        description: task.description,
        context: task.context,
        type: task.type,
        goalName,
        workstreamName,
        serviceName,
      });
      setInputs(inputsFromSuggestion(suggestion));
      setRationale(suggestion.rationale);
      setKbSources(suggestion.sources);
      setGrounded(suggestion.grounded);
      setSource('ai');
      // Land on the result so the engineer immediately sees Vector's suggested
      // Effort / SP / Confidence + the "why", then Accept or Modify.
      setGoToResultToken((t) => t + 1);
    } catch (e) {
      setAiError(friendlyEstimateError(e));
    } finally {
      setAiLoading(false);
    }
  };

  const onSave = async () => {
    if (!task) return;
    if (override) {
      const problems = validateOverride(override);
      if (problems.length > 0) {
        toast({
          tone: 'error',
          title: 'Fix the override',
          description: problems[0],
        });
        return;
      }
    }
    setSaving(true);
    try {
      const { task: updatedTask, estimate } = await save({
        task,
        inputs,
        result,
        source,
        rationale: source === 'ai' ? rationale : undefined,
        sources: source === 'ai' ? kbSources : undefined,
        override,
        existingEstimateId: existingEstimate?.id,
      });
      const eff = effectiveEstimate(
        {
          effortDays: result.engineeringEffortDays,
          storyPoints: result.storyPoints,
          confidence: result.confidence,
        },
        override,
      );
      toast({
        tone: 'success',
        title: eff.overridden ? 'Estimate saved (overridden)' : 'Estimate saved',
        description: `${eff.storyPoints} pts · ${eff.effortDays}d · ${eff.confidence}% confidence`,
      });
      onSaved(updatedTask, estimate);
      onClose();
    } catch (e) {
      toast({
        tone: 'error',
        title: 'Couldn’t save estimate',
        description: e instanceof Error ? e.message : 'Unknown error',
      });
    } finally {
      setSaving(false);
    }
  };

  if (!task) return null;

  return (
    <Modal
      open={open}
      onClose={close}
      size="lg"
      title={
        <span className="flex items-center gap-2">
          {existingEstimate ? 'Edit estimate' : 'Estimate'} ·{' '}
          <span className="font-normal text-zinc-400">{task.title}</span>
        </span>
      }
    >
      <div className="space-y-4">
        {/* ✨ Estimate with Vector */}
        <div className="rounded-xl border border-accent/30 bg-accent-soft/20 p-3">
          <div className="flex items-center justify-between gap-3">
            <div className="min-w-0">
              <p className="flex items-center gap-1.5 text-sm font-medium text-zinc-100">
                <Sparkles className="h-4 w-4 text-accent-fg" aria-hidden="true" />
                Estimate with Vector
              </p>
              <p className="mt-0.5 text-xs text-zinc-500">
                Vector checks the team knowledge base first, then suggests the
                answers below — the same engine computes the number, so you see
                exactly how it was derived.
              </p>
              {source === 'ai' && grounded && (
                <p className="mt-1 inline-flex items-center gap-1 text-xs text-emerald-300">
                  <BookOpen className="h-3 w-3" aria-hidden="true" />
                  Grounded in {kbSources.length} team KB source
                  {kbSources.length === 1 ? '' : 's'}
                </p>
              )}
            </div>
            <Button
              variant="secondary"
              size="sm"
              leftIcon={<Sparkles className="h-4 w-4" />}
              loading={aiLoading}
              onClick={runVector}
            >
              Suggest
            </Button>
          </div>
          {aiError && (
            <p className="mt-2 rounded-lg border border-amber-500/30 bg-amber-500/10 px-3 py-2 text-xs text-amber-200">
              {aiError}
            </p>
          )}
        </div>

        <EstimateWizard
          inputs={inputs}
          onChange={onWizardChange}
          result={result}
          onSave={onSave}
          saving={saving}
          saveLabel={existingEstimate ? 'Save changes' : 'Save Estimate'}
          aiRationale={source === 'ai' ? rationale : undefined}
          source={source}
          override={override}
          onOverrideChange={setOverride}
          kbSources={source === 'ai' ? kbSources : undefined}
          resetToken={resetToken}
          goToResultToken={goToResultToken}
        />
      </div>
    </Modal>
  );
}

export default EstimateFlowModal;
